/**
 * Best-effort React component-trail discovery via the fiber tree.
 *
 * Detail modes match the AFS 1.1 output levels:
 *   - compact:   none.
 *   - standard:  immediate component + 2 ancestors.
 *   - detailed:  up to 5 named ancestors.
 *   - forensic:  up to 12 named ancestors, deduplicated.
 */
const FIBER_KEY_PATTERNS = [
    /^__reactFiber\$/,
    /^__reactInternalInstance\$/,
];
const getFiber = (el) => {
    for (const key of Object.keys(el)) {
        if (FIBER_KEY_PATTERNS.some((p) => p.test(key))) {
            const value = el[key];
            if (value && typeof value === "object")
                return value;
        }
    }
    return null;
};
const nameOf = (type) => {
    if (!type)
        return null;
    if (typeof type === "string")
        return null;
    if (typeof type === "function") {
        const fn = type;
        const name = fn.displayName ?? fn.name;
        return name && name !== "Anonymous" ? name : null;
    }
    if (typeof type === "object") {
        const wrapped = type;
        if (wrapped.displayName)
            return wrapped.displayName;
        return nameOf(wrapped.render) ?? nameOf(wrapped.type);
    }
    return null;
};
const maxDepth = (detail) => {
    switch (detail) {
        case "compact":
            return 0;
        case "standard":
            return 3;
        case "detailed":
            return 5;
        case "forensic":
            return 12;
    }
};
export const getReactComponents = (el, detail = "standard") => {
    const limit = maxDepth(detail);
    if (limit === 0)
        return "";
    const fiber = getFiber(el);
    if (!fiber)
        return "";
    const trail = [];
    let current = fiber;
    while (current && trail.length < limit) {
        const name = nameOf(current.type);
        if (name && trail[trail.length - 1] !== name)
            trail.push(name);
        current = current.return;
    }
    return trail.join(" › ");
};
//# sourceMappingURL=reactFiber.js.map